/**
 * VariableSetInspector — Phase 6a-1.
 *
 * Edits a `variable_set` node — the workflow's simplest data-mutation
 * primitive. Source of truth for the field list is `soarNodes.js` →
 * DATA_NODES.variable_set.configSchema (3 fields):
 *
 *   variableName   string         Name of variable to set/update.
 *   value          json|template  Value to assign — accepts a JSON
 *                                  literal (string, number, object, array)
 *                                  OR a templated expression like
 *                                  `${node_id.path.to.field}`. The user
 *                                  types raw text; we save the parsed JSON
 *                                  when it parses, otherwise the raw string
 *                                  (matches the V3 behavior and the BE
 *                                  evaluator's tolerance for non-JSON
 *                                  literals).
 *   scope          enum           workflow | global. Defaults to `workflow`
 *                                  if unset — same default the BE applies.
 *
 * Mutation contract: per useCanvas:1098 we MUST spread the existing
 * `config` so any V3-only fields (`description`, `tags`, future fields)
 * survive the save round-trip. `setField` enforces that.
 *
 * This is the quickest of the Phase 6a inspectors (3 fields, no
 * sub-editors) and is intentionally the canonical reference for the
 * Phase 6 pattern — small, focused, no clever state, no derived
 * computed fields.
 */

import React from 'react';

const SCOPES = [
  { value: 'workflow', label: 'Workflow — variable visible only inside this workflow run' },
  { value: 'global',   label: 'Global — variable persists across runs of this workflow' },
];

export default function VariableSetInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const variableName = cfg.variableName || '';
  // `value` may be: undefined, a JSON literal (any type), or a raw string
  // (mid-typing or templated). Display layer always sees a string.
  const valueDisplay = stringifyValue(cfg.value);
  const scope = SCOPES.find((s) => s.value === cfg.scope)?.value || 'workflow';

  // Single-field mutation helper — spreads the existing config so
  // V3-only fields survive the save round-trip. See useCanvas:1098.
  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FieldGroup
        label="Variable Name"
        hint="Identifier — lowercase, underscores. Avoid spaces."
      >
        <input
          type="text"
          value={variableName}
          onChange={(e) => setField('variableName', e.target.value)}
          placeholder="my_variable"
          disabled={readOnly}
          autoComplete="off"
          spellCheck={false}
          style={inputStyle}
        />
      </FieldGroup>

      <FieldGroup
        label="Value"
        hint='JSON literal ("hello", 42, {"a":1}) or template (${node_id.field}).'
      >
        <textarea
          value={valueDisplay}
          onChange={(e) => setField('value', parseValueInput(e.target.value))}
          placeholder={'"hello" or 42 or {"a": 1} or ${upstream_node.result.field}'}
          disabled={readOnly}
          rows={4}
          spellCheck={false}
          style={{
            ...inputStyle,
            fontFamily: 'SFMono-Regular, Menlo, monospace',
            fontSize: 11,
            lineHeight: 1.4,
            resize: 'vertical',
          }}
        />
      </FieldGroup>

      <FieldGroup label="Scope">
        <select
          value={scope}
          onChange={(e) => setField('scope', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {SCOPES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </FieldGroup>
    </div>
  );
}

/* ───── helpers ───────────────────────────────────────────────── */

// Render whatever the BE / prior save left in `cfg.value` as a string
// the textarea can display. Strings pass through (so users keep typing
// in-progress templates without re-quoting). Non-string JSON values
// pretty-print so they're inspectable. undefined/null becomes empty.
function stringifyValue(v) {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch (_) { return String(v); }
}

// Inverse of stringifyValue — try JSON first, fall back to the raw
// string. Empty input means "unset". This matches V3 behavior and
// keeps the textarea forgiving while users type.
function parseValueInput(raw) {
  if (typeof raw !== 'string') return raw;
  if (raw.trim() === '') return undefined;
  try { return JSON.parse(raw); } catch (_) { return raw; }
}

/* ───── shared field UI ───────────────────────────────────────── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};

const selectStyle = {
  ...inputStyle,
  appearance: 'auto',
};
